<template>
    <div v-if="panel">
        <!-- Create Choice -->
        <div v-show="activeChoicesExist" class="row">
            <div class="col-12">
                <button
                    @click="createChoice(0)"
                    class="btn btn-light border-0 badge badge-light badge-pill badge-pill-lg mr-2 mt-2 mb-2"
                >
                    <span class="icon icon-glyphicons-basic-plus icon-mr align-middle"></span>
                    <span class="align-middle">{{ choiceDisplayText }}</span>
                </button>
            </div>
        </div>

        <!-- Choices -->
        <div v-for="(choice, index) in choices" :key="choice.id">
            <div v-show="!choice.is_deleted" class="rounded bg-offwhite p-3 my-3">
                <div class="d-flex">
                    <div class="flex-shrink-0 d-flex flex-column flex-grow-0 align-items-center mr-1">
                        <div class="mb-2">
                            <button type="button" class="btn btn-transparent py-0 px-1" @click="removeChoice(choice)">
                                <span class="icon icon-glyphicons-basic-bin"></span>
                            </button>
                        </div>
                        <div class="mb-3">
                            <div class="btn-group-vertical">
                                <button
                                    type="button"
                                    class="btn btn-transparent btn-sm py-0 px-1"
                                    @click="shiftChoiceIndex(index, -1)"
                                >
                                    <span class="icon icon-glyphicons-basic-arrow-up"></span>
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-transparent btn-sm py-0 px-1"
                                    @click="shiftChoiceIndex(index, 1)"
                                >
                                    <span class="icon icon-glyphicons-basic-arrow-down"></span>
                                </button>
                            </div>
                        </div>
                    </div>
                    <div class="flex-grow-1">
                        <!-- Choice Text -->
                        <div class="d-flex mb-3">
                            <div class="col-12">
                                <textarea
                                    v-model="choice.text"
                                    :placeholder="choiceDisplayText + ' ' + choiceIdentifierText"
                                    class="form-control form-control-sm"
                                    maxlength="1000"
                                    rows="2"
                                ></textarea>
                            </div>
                        </div>

                        <!-- Score & Correctness -->
                        <div class="d-flex mb-3" v-if="panel.panel_type != 'tabs'">
                            <div class="col-6">
                                <div class="pb-1 small font-weight-500">Score</div>
                                <input v-model="choice.score" placeholder="0" class="form-control form-control-sm" type="number" />
                            </div>
                            <div class="col-12 col-sm-6" v-show="showCorrectnessField">
                                <div class="pb-1 small font-weight-500">Correct/Incorrect</div>
                                <select v-model="choice.correct" class="form-control form-control-sm">
                                    <option :value="true">Correct</option>
                                    <option :value="false">Incorrect</option>
                                    <option :value="null">Neutral</option>
                                </select>
                            </div>
                            <div class="col-12 col-sm-6" v-show="panel.panel_type === 'categories'">
                                <div class="pb-1 small font-weight-500">Correct Category</div>
                                <select v-model="choice.correct_category_id" class="form-control form-control-sm">
                                    <option :value="null">None</option>
                                    <option
                                        v-for="category in panel.choice_categories"
                                        :key="category.id"
                                        :value="category.id || category.tempId"
                                    >
                                        {{ category.name }}
                                    </option>
                                </select>
                            </div>
                            <!-- Correct Order -->
                            <div class="col-12 col-sm-6" v-show="panel.panel_type === 'step_order'">
                                <div class="pb-1 small font-weight-500">Correct Order</div>
                                <select v-model="choice.correct_order" class="form-control form-control-sm">
                                    <option v-for="(choice, index) in panel.choices" :key="choice.id" :value="index">
                                        {{ index + 1 }}
                                    </option>
                                </select>
                            </div>
                        </div>

                        <div v-show="panel.panel_type === 'categories'">
                            <div class="d-flex">
                                <div class="col-12 col-sm-6">
                                    <div class="pb-1 small font-weight-500">Explanation</div>
                                    <select v-model="choice.action" class="form-control form-control-sm">
                                        <option value="none">None</option>
                                        <option value="show_content_modal">Content Pop-Up</option>
                                    </select>
                                </div>
                                <div class="col-12 col-sm-6" v-show="choice.action === 'show_content_modal'">
                                    <div class="pb-1 small font-weight-500">Pop-Up Width</div>
                                    <select v-model="choice.modal_width" class="form-control form-control-sm">
                                        <option
                                            v-for="option in modalWidthOptions"
                                            :key="option.value"
                                            :value="option.value"
                                        >
                                            {{ option.label }}
                                        </option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <!-- Actions -->
                        <div v-show="panel.panel_type === 'multichoice'">
                            <div class="d-flex">
                                <div class="col-6">
                                    <div class="pb-1 small font-weight-500">Action</div>
                                    <select v-model="choice.action" class="form-control form-control-sm">
                                        <option value="show_content_modal">Show a Pop-Up</option>
                                        <option value="proceed_to_node">Proceed to Screen</option>
                                    </select>
                                </div>
                                <div v-show="choice.action === 'proceed_to_node'" class="col-6">
                                    <div class="pb-1 small font-weight-500">Target Screen</div>
                                    <node-select
                                        v-model="choice.target_node"
                                        :node-options="nodeOptions"
                                        placeholder="Select a screen to proceed to"
                                    ></node-select>
                                </div>
                                <div v-show="['show_content_modal'].includes(choice.action)" class="col-6">
                                    <div class="pb-1 small font-weight-500">Pop-Up Width</div>
                                    <select v-model="choice.modal_width" class="form-control form-control-sm">
                                        <option
                                            v-for="option in modalWidthOptions"
                                            :key="option.value"
                                            :value="option.value"
                                        >
                                            {{ option.label }}
                                        </option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <!-- Choice Message -->
                        <div class="mt-2 mb-3" v-show="showMessageField">
                            <div class="d-flex">
                                <div class="col-12">
                                    <div class="pb-1 small font-weight-500">
                                        <span
                                            v-if="
                                                ['multiselect', 'singleselect', 'step_order'].includes(panel.panel_type)
                                            "
                                            >Explanation</span
                                        >
                                        <span v-else>Message</span>
                                    </div>
                                    <textarea
                                        v-model="choice.message"
                                        placeholder="Explanation or feedback message"
                                        rows="3"
                                        class="form-control form-control-sm"
                                        maxlength="2000"
                                    ></textarea>
                                </div>
                            </div>
                        </div>

                        <!-- Report Feedback -->
                        <div v-show="choice.showReportMessage || choice.report_message" class="mt-2">
                            <div class="d-flex">
                                <div class="col-12">
                                    <div class="pb-1 small font-weight-500">
                                        <span>Report Feedback</span>
                                        <help-text
                                            content="Add feedback to be shown only on the Learner Results Summary."
                                            class="ml-1"
                                        ></help-text>
                                    </div>
                                    <textarea
                                        v-model="choice.report_message"
                                        class="form-control form-control-sm"
                                        rows="3"
                                        placeholder="Report feedback or rationale message"
                                    ></textarea>
                                </div>
                            </div>
                        </div>

                        <!-- Button Badges -->
                        <div class="mt-3 mb-1 d-flex">
                            <div class="col-12 d-flex">
                                <div
                                    v-show="choice.action === 'show_content_modal' || panel.panel_type === 'tabs'"
                                    class="mr-1"
                                >
                                    <button
                                        class="btn btn-gray badge badge-gray"
                                        @click="openChoiceElementEditor(index)"
                                    >
                                        <span class="icon icon-glyphicons-basic-pencil icon-mr"></span>
                                        <span>Edit Content</span>
                                    </button>
                                </div>
                                <div
                                    v-show="
                                        ['multiselect', 'singleselect', 'multichoice', 'step_order'].includes(
                                            panel.panel_type
                                        )
                                    "
                                >
                                    <button
                                        v-show="!(choice.showReportMessage || choice.report_message)"
                                        class="btn btn-gray badge badge-gray"
                                        @click="showReportMessage(choice)"
                                    >
                                        <span
                                            class="icon icon-100 icon-glyphicons-basic-plus icon-mr align-middle"
                                        ></span>
                                        <span>Report Feedback</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Choice Elements -->
            <div class="collapse" :id="'choice-content-' + index">
                <div class="d-flex" v-if="choice.action === 'show_content_modal' || panel.panel_type === 'tabs'">
                    <div class="flex-grow-0">
                        <div class="ml-4 mt-3">
                            <span
                                class="text-info icon icon-glyphicons-basic-step-forward align-middle content-arrow icon-flipped"
                            ></span>
                        </div>
                    </div>
                    <div class="flex-grow-1">
                        <div class="d-flex pl-2 pr-2">
                            <div class="col-12">
                                <rich-content-editor
                                    v-model="choice.elements"
                                    ref="richContentEditor"
                                    :show-info-footers="false"
                                    :centered="false"
                                    :allowed-video-fields="['autoplay_video', 'loop_media', 'hide_video_controls']"
                                    :allowed-audio-fields="['accessibility_alt_text', 'autoplay_video', 'loop_media']"
                                    :narrow-mode="true"
                                ></rich-content-editor>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Create Choice -->
            <div v-show="!choice.is_deleted" class="row">
                <div class="col-12">
                    <button
                        @click="createChoice(index + 1)"
                        class="btn btn-light border-0 badge badge-light badge-pill badge-pill-lg mr-2 mt-2 mb-2"
                    >
                        <span class="icon icon-glyphicons-basic-plus icon-mr align-middle"></span>
                        <span class="align-middle">{{ choiceDisplayText }}</span>
                    </button>
                </div>
            </div>
        </div>

        <!-- Empty Message -->
        <div v-show="!activeChoicesExist" class="py-5">
            <p class="text-secondary text-center">No {{ choiceDisplayText.toLowerCase() }}s created yet.</p>
            <div class="text-center">
                <button @click="createChoice(0)" class="btn btn-light border-0 badge badge-light badge-pill badge-pill-lg">
                    <span class="icon icon-glyphicons-basic-plus icon-mr align-middle"></span>
                    <span class="align-middle">{{ choiceDisplayText }}</span>
                </button>
            </div>
        </div>
        <br />
    </div>
</template>

<script>
import RichContentEditor from "@/components/RichContentEditor"
import HelpText from "@/components/HelpText"
import NodeSelect from "@/components/NodeSelect"

export default {
    name: "ChoicePanelEditor",
    components: {
        RichContentEditor,
        HelpText,
        NodeSelect,
    },
    props: {
        value: {
            type: Object,
            required: false,
        },
        choices: {
            type: Array,
            required: false,
            default() {
                return []
            },
        },
        nodeOptions: {
            type: Array,
            required: false,
        },
    },
    data() {
        return {
            panel: null,
            openChoiceContentIndex: null,
            modalWidthOptions: [
                {
                    label: "Small",
                    value: "medium",
                },
                {
                    label: "Medium",
                    value: "large",
                },
                {
                    label: "Large",
                    value: "extra_large",
                },
            ],
        }
    },
    computed: {
        showCorrectnessField() {
            return ["multiselect", "singleselect", "multichoice"].includes(this.panel.panel_type)
        },
        activeChoicesExist() {
            return this.choices && this.choices.filter((c) => c.is_deleted != true).length > 0
        },
        choiceDisplayText() {
            if (!this.panel) {
                return "Choice"
            }
            if (this.panel.panel_type === "multichoice") {
                return "Button"
            } else if (this.panel.panel_type === "step_order") {
                return "Step"
            } else if (this.panel.panel_type === "categories") {
                return "Item"
            } else if (this.panel.panel_type === "tabs") {
                return "Tab"
            }
            return "Choice"
        },
        choiceIdentifierText() {
            if (this.panel.panel_type == "tabs") {
                return "name"
            }
            return "text"
        },
        showMessageField() {
            return !["multichoice", "categories", "tabs"].includes(this.panel.panel_type)
        },
    },
    watch: {
        // Respond to changes from the parent component.
        value() {
            this.panel = this.value
        },
    },
    methods: {
        // Create a new choice.
        createChoice(index) {
            let action = "show_text"
            if (this.panel.panel_type === "categories") {
                action = "none"
            } else if (["multichoice", "tabs"].includes(this.panel.panel_type)) {
                action = "show_content_modal"
            }
            let choice = {
                text: "",
                action: action,
                message: "",
                target_node: null,
                correct: null,
                correct_category_id: null,
                modal_width: "medium",
                elements: [],
            }
            this.panel.choices.splice(index, 0, choice)
        },
        shiftChoiceIndex(index, increment) {
            // Don't do anything if choice is already at the start or end of the array.
            if ((index == 0 && increment == -1) || (index + 1 >= this.panel.choices.length && increment == 1)) {
                return
            }
            // Remove the choice from the choice array.
            let choice = this.panel.choices.splice(index, 1)[0]
            // Put it back at the new position.
            this.panel.choices.splice(index + increment, 0, choice)
        },
        // Set deleted flag to true for the choice.
        removeChoice(choice) {
            this.$set(choice, "is_deleted", true)
        },
        // Reveals the element editor associated with a choice, by its index.
        openChoiceElementEditor(index) {
            if (index === this.openChoiceContentIndex) {
                this.$nextTick(() => $("#choice-content-" + index).collapse("toggle"))
            } else {
                // Close currently open content editor.
                $("#choice-content-" + this.openChoiceContentIndex).collapse("hide")
                // Open the selected content editor.
                this.$nextTick(() => $("#choice-content-" + index).collapse("show"))
                this.openChoiceContentIndex = index
            }
        },
        showReportMessage(choice) {
            this.$set(choice, "showReportMessage", true)
        },
    },
    mounted() {
        this.panel = this.value
    },
}
</script>

<style lang="scss" scoped>
.content-arrow {
    font-size: 32px;
}
</style>